import { useEffect, useState } from "react";
import { cornerRightUpArrow, leaveRequestIcon } from "../../asset";
import { LeaveRequestItem } from "../../types/dashboardType";
import LeaveRequestModal from "../../components/modal/LeaveRequestModal";
import { updateLeaveStatus } from "../../features/leave-request/leaveRequestSlice";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { fetchDashboardData } from "../../features/dashboard/dashboardSlice";
import { useBranchCode } from "../../util/hooks/useBranch";
import { toast } from "sonner";

interface Props {
  leaveRequests: LeaveRequestItem[];
}

export default function LeaveRequest({ leaveRequests }: Props) {
  const [activeTab, setActiveTab] = useState<"staff" | "student">("staff");
  const [openModal, setOpenModal] = useState(false);

  const { confirm, modal } = useConfirmToast();
  const dispatch = useDispatch<AppDispatch>();

  const staffLeaves = leaveRequests.filter((item) => item.staffId !== null);

  const studentLeaves = leaveRequests.filter((item) => item.studentId !== null);

  const hasStaff = staffLeaves.length > 0;
  const hasStudent = studentLeaves.length > 0;
  const branchCode = useBranchCode();

  const filteredData = activeTab === "staff" ? staffLeaves : studentLeaves;

  useEffect(() => {
    if (hasStaff) {
      setActiveTab("staff");
    } else if (hasStudent) {
      setActiveTab("student");
    }
  }, [leaveRequests]);

  const handleStatusChange = async (
    leaveRequestId: number,
    status: "APPROVED" | "REJECTED",
  ) => {
    const ok = await confirm(
      `Are you sure you want to ${status.toLowerCase()} this leave?`,
      { confirmText: status === "APPROVED" ? "Approve" : "Reject" },
    );

    if (!ok) return;

    try {
      // Update leave status
      await dispatch(updateLeaveStatus({ leaveRequestId, status })).unwrap();

      toast.success(
        status === "APPROVED"
          ? "Leave approved successfully"
          : "Leave rejected successfully",
      );

      // REFRESH
      dispatch(fetchDashboardData({ branchCode }));
    } catch (error) {
      console.error("Failed to update leave status", error);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-[0_0_54px_0_rgba(0,0,0,0.05)] ">
      {/* Header */}
      <div className="flex justify-between items-center gap-4 p-3">
        <div className="flex gap-3 items-center">
          <img
            src={leaveRequestIcon}
            alt="Upcoming Events"
            className="w-8 h-8 shrink-0"
          />
          <p className="text-base text-neutral-500 font-medium text-nowrap">
            Leave Requests
          </p>
        </div>
        <button
          onClick={() => setOpenModal(true)}
          className="underline text-sm text-primary-600 font-medium"
        >
          View All
        </button>
      </div>

      {/* Empty State */}
      {!hasStaff && !hasStudent ? (
        /* GLOBAL EMPTY */
        <div className="flex flex-col gap-4 pt-5 pb-2 justify-center items-center h-[300px]">
          <img
            src={cornerRightUpArrow}
            alt="no queries"
            className="w-10 h-10 border-2 rounded-full p-2 border-neutral-200"
          />
          <div className="flex flex-col gap-2 items-center text-center">
            <p className="text-neutral-300 text-base font-medium text-center px-10 underline">
              No Leave Applications Today!
            </p>
            <p className="text-[13px] font-normal text-neutral-300">
              Everything is running as scheduled.
            </p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Tabs */}
          <div className="flex items-center w-full justify-evenly text-xs font-semibold">
            <button
              onClick={() => setActiveTab("staff")}
              className={`w-full py-[10px] transition-colors ${
                activeTab === "staff"
                  ? "bg-error-400 text-white"
                  : "text-neutral-300"
              }`}
            >
              Staffs
            </button>

            <button
              onClick={() => setActiveTab("student")}
              className={`w-full py-[10px] transition-colors ${
                activeTab === "student"
                  ? "bg-error-400 text-white"
                  : "text-neutral-300"
              }`}
            >
              Students
            </button>
          </div>

          <div className="p flex flex-col h-[370px] gap-6 overflow-y-auto custom-scrollbar px-5">
            {filteredData.length === 0 ? (
              <div className="flex flex-col gap-2 items-center pt-10 justify-center text-center">
                <p className="text-neutral-300 text-base font-medium underline">
                  No {activeTab === "staff" ? "Staff" : "Student"} Leave
                  Applications!
                </p>
                <p className="text-[13px] font-normal text-neutral-300">
                  Everything is running as scheduled.
                </p>
              </div>
            ) : (
              filteredData.map((item) => {
                const name =
                  activeTab === "staff" ? item.staffName : item.studentName;

                return (
                  <div
                    key={item.leaveRequestId}
                    className="flex items-start gap-[16px]"
                  >
                    <div className="w-11 h-11 rounded-full shrink-0 bg-primary-400 flex items-center justify-center text-white font-semibold text-lg">
                      {name?.charAt(0)?.toUpperCase() || "?"}
                    </div>

                    <div className="flex flex-col">
                      <p className="text-[15px] text-neutral-400">
                        <span className="font-semibold">{name}</span> is
                        requesting a leave.
                      </p>
                      <p className="text-neutral-300 text-[13px] line-clamp-2">
                        {item.reason}
                      </p>
                      <div className="grid grid-cols-2 gap-4 mt-[10px]">
                        <button
                          onClick={() =>
                            handleStatusChange(item.leaveRequestId, "APPROVED")
                          }
                          className="text-[13px] text-white rounded bg-primary-600 py-2 px-4"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() =>
                            handleStatusChange(item.leaveRequestId, "REJECTED")
                          }
                          className="text-[13px] text-error-600 bg-error-50 rounded py-2 px-4"
                        >
                          Deny
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
      {modal}
      {openModal && (
        <LeaveRequestModal
          leaveRequests={leaveRequests}
          onClose={() => setOpenModal(false)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}
